/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import {
  BackstageIdentityResponse,
  SignInPageProps,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useState } from 'react';
import { useMountEffect } from '@react-hookz/web';
import {
  Content,
  ContentHeader,
  IdentityProviders,
  InfoCard,
  Page,
  Progress,
  SignInProviderConfig,
  UserIdentity,
} from '@backstage/core-components';
import { getSignInProviders, useSignInProviders } from './providers';
import LogoVertical from '../Root/LogoVertical';
import { Box } from '@material-ui/core';
import { getPortalTitle } from '../../utils/configUtility';

type MultiSignInPageProps = SignInPageProps & {
  providers: IdentityProviders;
  title?: string;
  align?: 'center' | 'left';
};

type SingleSignInPageProps = SignInPageProps & {
  provider: SignInProviderConfig;
  auto?: boolean;
};

const getPortalDescription = (portalName: string) => {
  return `${portalName} is a centralized interface for all the information about Services, APIs, Resources and Documentation.`;
};
export type Props = MultiSignInPageProps | SingleSignInPageProps;

export const MultiSignInPage = ({
  onSignInSuccess,
  providers = [],
  title,
  align = 'left',
}: MultiSignInPageProps) => {
  const signInProviders = getSignInProviders(providers);
  const portalName = getPortalTitle(useApi(configApiRef));
  const [loading, providerElements] = useSignInProviders(
    signInProviders,
    onSignInSuccess,
  );

  if (loading) {
    return <Progress />;
  }

  return (
    <Page themeId="home">
      <Content>
        {title && <ContentHeader title={title} textAlign={align} />}
        <Grid
          container
          justifyContent={align === 'center' ? align : 'flex-start'}
          direction="row"
          alignItems="center"
        >
          <Grid item xs={12} md={6}>
            <InfoCard divider={false}>
              <Grid container>
                <Grid item xs={12} md={5}>
                  <Box sx={{ bgcolor: '#05322B', height: '100%' }}>
                    <LogoVertical />
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography variant="h1">{portalName}</Typography>
                  <Box my={4}>
                    <Typography variant="subtitle1">
                      {getPortalDescription(portalName)}
                    </Typography>
                  </Box>
                  <Grid container spacing={2} direction="column">
                    {providerElements}
                  </Grid>
                </Grid>
              </Grid>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

const renderError = (error?: Error) => {
  return (
    error &&
    error.name !== 'PopupRejectedError' && (
      <Typography variant="body1" color="error">
        {error.message}
      </Typography>
    )
  );
};

export const SingleSignInPage = ({
  provider,
  auto,
  onSignInSuccess,
}: SingleSignInPageProps) => {
  const authApi = useApi(provider.apiRef);

  const [error, setError] = useState<Error>();
  const portalName = getPortalTitle(useApi(configApiRef));

  // The SignIn component takes some time to decide whether the user is logged-in or not.
  // showLoginPage is used to prevent a glitch-like experience where the sign-in page is
  // displayed for a split second when the user is already logged-in.
  const [showLoginPage, setShowLoginPage] = useState<boolean>(false);

  type LoginOpts = { checkExisting?: boolean; showPopup?: boolean };
  const login = async ({ checkExisting, showPopup }: LoginOpts) => {
    try {
      let identityResponse: BackstageIdentityResponse | undefined;
      if (checkExisting) {
        // Do an initial check if any logged-in session exists
        identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });
      }

      // If no session exists, show the sign-in page
      if (!identityResponse && (showPopup || auto)) {
        // Unless auto is set to true, this step should not happen.
        // When user intentionally clicks the Sign In button, autoShowPopup is set to true
        setShowLoginPage(true);
        identityResponse = await authApi.getBackstageIdentity({
          instantPopup: true,
        });
        if (!identityResponse) {
          throw new Error(
            `The ${provider.title} provider is not configured to support sign-in`,
          );
        }
      }

      if (!identityResponse) {
        setShowLoginPage(true);
        return;
      }

      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (err: any) {
      // User closed the sign-in modal
      setError(err);
      setShowLoginPage(true);
    }
  };

  useMountEffect(() => login({ checkExisting: true }));

  if (!showLoginPage) {
    return <Progress />;
  }

  return (
    <Page themeId="home">
      <Content>
        <Grid
          container
          justifyContent="center"
          direction="row"
          alignItems="center"
        >
          <Grid item xs={12} md={6}>
            <InfoCard divider={false}>
              <Grid container>
                <Grid item xs={12} md={5}>
                  <Box sx={{ bgcolor: '#05322B', height: '100%' }}>
                    <LogoVertical />
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography variant="h1">{portalName}</Typography>
                  <Box my={4}>
                    <Typography variant="subtitle1">
                      {getPortalDescription(portalName)}
                    </Typography>
                  </Box>
                  {renderError(error)}
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      login({ showPopup: true });
                    }}
                  >
                    Sign In {provider.title}
                  </Button>
                </Grid>
              </Grid>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export function SignInPage(props: Props) {
  if ('provider' in props) {
    return <SingleSignInPage {...props} />;
  }

  return <MultiSignInPage {...props} />;
}
